import { useMemo } from 'react'
import {
  CellStyle,
  ColDef,
  ColGroupDef,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import { intl } from '../../../../i18n'
import { ColumnType } from '../../../containers/commons/AgGrid'
import { ClientSideSelectFilter } from '../../../containers/BulkSheetView/components/filter'
import { colorPalette } from '../../../style/colorPalette'
import { useRowNumber } from '../../../page-properties/bulksheet-properties/rowNumber'
import { useCode } from '../../../page-properties/bulksheet-properties/common'
import {
  useCreatedAt,
  useCreatedBy,
  useRevision,
  useUpdatedAt,
  useUpdatedBy,
} from '../../../page-properties/bulksheet-properties/changeLog'
import {
  PROGRAM_PROJECT_REPORT_ROW_TYPE,
  ProgramProjectReportProfitLoss,
  ProgramProjectReportProgress,
  ProgramProjectReportResource,
  ProgramProjectReportRow,
  ProgramProjectReportRowType,
} from '../model/reportRow'
import {
  ProductivityMetric,
  calcExpectedProfitRatio,
  calcProductivity,
  calcProgressRate,
  calcRemaining,
  calcScheduledProgressRate,
} from './calc'
import {
  WbsItemStatus,
  toLabel as toWbsStatusLabel,
} from '../../../../domain/entity/WbsItemEntity'
import { ProjectStatus } from '../../../../lib/functions/project'

type RowValueGetter = ValueGetterParams<ProgramProjectReportRow>

const NUMBER_COLUMN_TYPE = [ColumnType.number]
const DATE_COLUMN_TYPE = [ColumnType.date]

const wbsStatusLabelByValue: Record<string, string> = Object.fromEntries(
  Object.values(WbsItemStatus).map(status => [status, toWbsStatusLabel(status)])
)

export const statusFilterOptions = [
  ...Object.values(ProjectStatus),
  ...Object.values(WbsItemStatus).map(toWbsStatusLabel),
]

const statusSelectFilter = {
  filter: ClientSideSelectFilter,
  filterParams: {
    optionKey: 'programProjectReportStatuses',
    getValuesForOptionFilter: async (context: {
      programProjectReportStatuses: string[]
    }) => context.programProjectReportStatuses,
    getValue: (option: string) => option,
    getLabel: (option: string) => option,
  },
}

const GRAYED_OUT_STYLE: CellStyle = {
  backgroundColor: colorPalette.monotone[2],
  color: colorPalette.monotone[3],
  justifyContent: 'flex-end',
}

const RIGHT_ALIGN_STYLE: CellStyle = { justifyContent: 'flex-end' }

const isProjectRow = (data?: ProgramProjectReportRow | null): boolean =>
  data?.type === PROGRAM_PROJECT_REPORT_ROW_TYPE.PROJECT

const projectOnlyCellStyle = (params: {
  data?: ProgramProjectReportRow | null
}): CellStyle =>
  isProjectRow(params.data) ? RIGHT_ALIGN_STYLE : GRAYED_OUT_STYLE

const numberValueFormatter = (params: ValueFormatterParams): string =>
  params.value == null ? '' : Number(params.value).toLocaleString()

const decimalValueFormatter = (params: ValueFormatterParams): string =>
  params.value == null
    ? ''
    : Number(params.value).toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })

const percentValueFormatter = (params: ValueFormatterParams): string =>
  params.value == null ? '' : `${(Number(params.value) * 100).toFixed(1)}%`

const getProgress = (
  data?: ProgramProjectReportRow | null
): ProgramProjectReportProgress | null => data?.progress ?? null

const getResource = (
  data?: ProgramProjectReportRow | null
): ProgramProjectReportResource | null => data?.resource ?? null

const getProfitLoss = (
  data?: ProgramProjectReportRow | null
): ProgramProjectReportProfitLoss | null => data?.profitLoss ?? null

const typeLabelKey: Record<ProgramProjectReportRowType, string> = {
  [PROGRAM_PROJECT_REPORT_ROW_TYPE.PROJECT]:
    'programProjectReport.type.project',
  [PROGRAM_PROJECT_REPORT_ROW_TYPE.WORKGROUP]:
    'programProjectReport.type.workgroup',
  [PROGRAM_PROJECT_REPORT_ROW_TYPE.PROCESS]:
    'programProjectReport.type.process',
}

// type: 固定 enum なので、行の有無に関わらず全候補を表示する。
// 候補は context.programProjectReportTypes（gridOptions で注入）から取得する
const typeSelectFilter = {
  filter: ClientSideSelectFilter,
  filterParams: {
    optionKey: 'programProjectReportTypes',
    getValuesForOptionFilter: async (context: {
      programProjectReportTypes: ProgramProjectReportRowType[]
    }) => context.programProjectReportTypes,
    valueGetter: ({ node }: { node: IRowNode<ProgramProjectReportRow> }) =>
      node?.data?.type ?? '',
    getValue: (option: string) => option,
    getLabel: (option: ProgramProjectReportRowType) =>
      intl.formatMessage({ id: typeLabelKey[option] }),
  },
}

const buildBasicColumnGroup = (
  codeCol: ColDef<ProgramProjectReportRow>
): ColGroupDef<ProgramProjectReportRow> => ({
  headerName: intl.formatMessage({ id: 'programProjectReport.column.basic' }),
  children: [
    { ...codeCol, colId: 'code', hide: false, width: 100, pinned: 'left' },
    {
      colId: 'type',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.type',
      }),
      width: 110,
      pinned: 'left',
      valueGetter: (params: RowValueGetter) => {
        const type = params.data?.type
        return type ? intl.formatMessage({ id: typeLabelKey[type] }) : ''
      },
      ...typeSelectFilter,
    },
    {
      colId: 'status',
      field: 'status',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.status',
      }),
      width: 110,
      pinned: 'left',
      valueGetter: (params: RowValueGetter) => {
        const { data } = params
        const status = data?.status
        if (!status) return ''
        // PROJECT は ProjectStatus を生表示（既存踏襲）、WORKGROUP/PROCESS は WBS ラベルに変換
        if (data?.type === PROGRAM_PROJECT_REPORT_ROW_TYPE.PROJECT) {
          return status
        }
        return wbsStatusLabelByValue[status] ?? status
      },
      ...statusSelectFilter,
    },
  ],
})

const periodColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({ id: 'programProjectReport.column.period' }),
  children: [
    {
      colId: 'scheduledStartDate',
      field: 'scheduledStartDate',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.scheduledStartDate',
      }),
      type: DATE_COLUMN_TYPE,
      width: 110,
    },
    {
      colId: 'scheduledEndDate',
      field: 'scheduledEndDate',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.scheduledEndDate',
      }),
      type: DATE_COLUMN_TYPE,
      width: 110,
    },
  ],
}

// progress.X を素の数値として表示する列（rate/remaining は計算が要るので個別定義）
const progressValueColumn = (
  field: keyof ProgramProjectReportProgress,
  headerId: string,
  width: number
): ColDef<ProgramProjectReportRow> => ({
  colId: `progress.${field}`,
  headerName: intl.formatMessage({ id: headerId }),
  type: NUMBER_COLUMN_TYPE,
  width,
  cellStyle: RIGHT_ALIGN_STYLE,
  valueGetter: (params: RowValueGetter) =>
    getProgress(params.data)?.[field] ?? null,
  valueFormatter: numberValueFormatter,
})

const progressColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({
    id: 'programProjectReport.column.progress',
  }),
  children: [
    {
      colId: 'progress.scheduledProgressRate',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.progress.scheduledProgressRate',
      }),
      type: NUMBER_COLUMN_TYPE,
      width: 90,
      cellStyle: RIGHT_ALIGN_STYLE,
      valueGetter: (params: RowValueGetter) => {
        const progress = getProgress(params.data)
        return calcScheduledProgressRate(
          progress?.scheduledCompleted ?? null,
          progress?.total ?? null
        )
      },
      valueFormatter: percentValueFormatter,
    },
    {
      colId: 'progress.progressRate',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.progress.progressRate',
      }),
      type: NUMBER_COLUMN_TYPE,
      width: 80,
      cellStyle: RIGHT_ALIGN_STYLE,
      valueGetter: (params: RowValueGetter) => {
        const progress = getProgress(params.data)
        return calcProgressRate(
          progress?.completed ?? null,
          progress?.total ?? null
        )
      },
      valueFormatter: percentValueFormatter,
    },
    progressValueColumn(
      'total',
      'programProjectReport.column.progress.total',
      70
    ),
    progressValueColumn(
      'scheduledCompleted',
      'programProjectReport.column.progress.scheduledCompleted',
      80
    ),
    progressValueColumn(
      'completed',
      'programProjectReport.column.progress.completed',
      70
    ),
    progressValueColumn(
      'delayed',
      'programProjectReport.column.progress.delayed',
      70
    ),
    progressValueColumn(
      'preceding',
      'programProjectReport.column.progress.preceding',
      70
    ),
    {
      colId: 'progress.remaining',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.progress.remaining',
      }),
      type: NUMBER_COLUMN_TYPE,
      width: 70,
      cellStyle: RIGHT_ALIGN_STYLE,
      valueGetter: (params: RowValueGetter) => {
        const progress = getProgress(params.data)
        return calcRemaining(
          progress?.total ?? null,
          progress?.completed ?? null
        )
      },
      valueFormatter: numberValueFormatter,
    },
  ],
}

const productivityColumn = (
  metric: ProductivityMetric,
  headerId: string,
  formatter: (params: ValueFormatterParams) => string
): ColDef<ProgramProjectReportRow> => ({
  colId: `productivity.${metric}`,
  headerName: intl.formatMessage({ id: headerId }),
  type: NUMBER_COLUMN_TYPE,
  width: 80,
  cellStyle: projectOnlyCellStyle,
  valueGetter: (params: RowValueGetter) => {
    const progress = getProgress(params.data)
    const resource = getResource(params.data)
    return calcProductivity(metric, {
      total: progress?.total ?? null,
      scheduledCompleted: progress?.scheduledCompleted ?? null,
      completed: progress?.completed ?? null,
      actualResource: resource?.actualResource ?? null,
    })
  },
  valueFormatter: formatter,
})

const productivityColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({
    id: 'programProjectReport.column.productivity',
  }),
  children: [
    productivityColumn(
      'sv',
      'programProjectReport.column.productivity.sv',
      decimalValueFormatter
    ),
    productivityColumn(
      'spi',
      'programProjectReport.column.productivity.spi',
      percentValueFormatter
    ),
    productivityColumn(
      'cv',
      'programProjectReport.column.productivity.cv',
      decimalValueFormatter
    ),
    productivityColumn(
      'cpi',
      'programProjectReport.column.productivity.cpi',
      percentValueFormatter
    ),
    productivityColumn(
      'etc',
      'programProjectReport.column.productivity.etc',
      decimalValueFormatter
    ),
    productivityColumn(
      'eac',
      'programProjectReport.column.productivity.eac',
      decimalValueFormatter
    ),
  ],
}

const resourceColumn = (
  field: keyof ProgramProjectReportResource,
  headerId: string
): ColDef<ProgramProjectReportRow> => ({
  colId: `resource.${field}`,
  headerName: intl.formatMessage({ id: headerId }),
  type: NUMBER_COLUMN_TYPE,
  width: 90,
  cellStyle: projectOnlyCellStyle,
  valueGetter: (params: RowValueGetter) => {
    if (!isProjectRow(params.data)) return null
    return getResource(params.data)?.[field] ?? null
  },
  valueFormatter: numberValueFormatter,
})

const resourceColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({
    id: 'programProjectReport.column.resource',
  }),
  children: [
    resourceColumn(
      'totalResource',
      'programProjectReport.column.resource.totalResource'
    ),
    resourceColumn(
      'investedResource',
      'programProjectReport.column.resource.investedResource'
    ),
    resourceColumn(
      'scheduledResource',
      'programProjectReport.column.resource.scheduledResource'
    ),
    resourceColumn(
      'actualResource',
      'programProjectReport.column.resource.actualResource'
    ),
  ],
}

const profitLossColumn = (
  field: keyof ProgramProjectReportProfitLoss,
  headerId: string
): ColDef<ProgramProjectReportRow> => ({
  colId: `profitLoss.${field}`,
  headerName: intl.formatMessage({ id: headerId }),
  type: NUMBER_COLUMN_TYPE,
  width: 100,
  cellStyle: projectOnlyCellStyle,
  valueGetter: (params: RowValueGetter) => {
    if (!isProjectRow(params.data)) return null
    return getProfitLoss(params.data)?.[field] ?? null
  },
  valueFormatter: numberValueFormatter,
})

const profitLossColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({
    id: 'programProjectReport.column.profitLoss',
  }),
  children: [
    profitLossColumn(
      'totalBudget',
      'programProjectReport.column.profitLoss.totalBudget'
    ),
    profitLossColumn(
      'scheduledUsedBudget',
      'programProjectReport.column.profitLoss.scheduledUsedBudget'
    ),
    profitLossColumn(
      'actualUsedBudget',
      'programProjectReport.column.profitLoss.actualUsedBudget'
    ),
    profitLossColumn(
      'remainingBudget',
      'programProjectReport.column.profitLoss.remainingBudget'
    ),
    profitLossColumn(
      'totalRevenue',
      'programProjectReport.column.profitLoss.totalRevenue'
    ),
    profitLossColumn(
      'scheduledRevenue',
      'programProjectReport.column.profitLoss.scheduledRevenue'
    ),
    profitLossColumn(
      'actualRevenue',
      'programProjectReport.column.profitLoss.actualRevenue'
    ),
    profitLossColumn(
      'forecastRevenue',
      'programProjectReport.column.profitLoss.forecastRevenue'
    ),
    {
      colId: 'profitLoss.expectedProfitRatio',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.profitLoss.expectedProfitRatio',
      }),
      type: NUMBER_COLUMN_TYPE,
      width: 100,
      cellStyle: projectOnlyCellStyle,
      valueGetter: (params: RowValueGetter) => {
        if (!isProjectRow(params.data)) return null
        const profitLoss = getProfitLoss(params.data)
        return calcExpectedProfitRatio(
          profitLoss?.forecastRevenue ?? null,
          profitLoss?.actualUsedBudget ?? null,
          profitLoss?.remainingBudget ?? null
        )
      },
      valueFormatter: percentValueFormatter,
    },
  ],
}

export const useColumnDefs = (): (
  | ColDef<ProgramProjectReportRow>
  | ColGroupDef<ProgramProjectReportRow>
)[] => {
  const rowNumberCol = useRowNumber()
  const codeCol = useCode('code')
  const revisionCol = useRevision('revision')
  const createdAtCol = useCreatedAt('createdAt')
  const createdByCol = useCreatedBy('createdBy')
  const updatedAtCol = useUpdatedAt('updatedAt')
  const updatedByCol = useUpdatedBy('updatedBy')

  return useMemo(
    () => [
      { field: 'uuid', hide: true, suppressColumnsToolPanel: true },
      rowNumberCol,
      buildBasicColumnGroup(codeCol),
      periodColumnGroup,
      progressColumnGroup,
      productivityColumnGroup,
      resourceColumnGroup,
      profitLossColumnGroup,
      {
        headerName: intl.formatMessage({ id: 'changeLog' }),
        children: [
          revisionCol,
          createdAtCol,
          createdByCol,
          updatedAtCol,
          updatedByCol,
        ],
      },
    ],
    [
      rowNumberCol,
      codeCol,
      revisionCol,
      createdAtCol,
      createdByCol,
      updatedAtCol,
      updatedByCol,
    ]
  )
}
